import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { X, Filter, Search, Check } from 'lucide-react';
import ModalPortal from './ModalPortal';
import { useScrollLock } from '../hooks/useScrollLock';
import { apiFetch } from '../utils/apiUtils';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  width: 95%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  max-height: 60vh;
  overflow-y: auto;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  color: #374151;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  color: #374151;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
`;

const ValuesList = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
`;

const ValueItem = styled.div`
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  border-bottom: 1px solid #e5e7eb;
  transition: background-color 0.15s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f3f4f6;
  }
  
  ${props => props.selected && `
    background: #eff6ff;
    
    &:hover {
      background: #dbeafe;
    }
  `}
`;

const ValueText = styled.span`
  color: #374151;
  font-size: 0.875rem;
  flex: 1;
`;

const ValueCount = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
  margin-right: 0.5rem;
`;

const CheckIcon = styled.div`
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const SelectedCount = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Button = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
  
  ${props => props.variant === 'primary' && `
    background: #3b82f6;
    color: white;
    
    &:hover:not(:disabled) {
      background: #2563eb;
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;
    
    &:hover {
      background: #f9fafb;
    }
  `}
`;

const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
`;

const LoadingState = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
`;

const MatchStatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-top: 0.75rem;
`;

const MatchStatusOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 2px solid ${props => props.selected ? props.color : '#e5e7eb'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${props => props.selected ? `${props.color}15` : 'white'};
  
  &:hover {
    border-color: ${props => props.color};
    background: ${props => `${props.color}10`};
  }
`;

const MatchStatusColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
`;

const MatchStatusLabel = styled.span`
  color: #374151;
  font-size: 0.875rem;
  font-weight: 500;
  flex: 1;
`;

const AdvancedFiltersModal = ({ isOpen, onClose, onApply, activeFields, currentFilter }) => {
  useScrollLock(isOpen);
  
  const [selectedField, setSelectedField] = useState('');
  const [availableValues, setAvailableValues] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [selectedMatchStatuses, setSelectedMatchStatuses] = useState([]); // 'perfect', 'maybe', 'mismatch', 'no_data'
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  // Initialize or reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (currentFilter) {
        // Restore existing filter
        setSelectedField(currentFilter.fieldName);
        setSelectedValues(currentFilter.values || []);
        setSelectedMatchStatuses(currentFilter.matchStatuses || []);
      } else {
        // Reset to empty
        setSelectedField('');
        setAvailableValues([]);
        setSelectedValues([]);
        setSelectedMatchStatuses([]);
      }
      setSearchTerm('');
    }
  }, [isOpen, currentFilter]);

  // Fetch available values when field is selected
  useEffect(() => {
    const fetchFieldValues = async () => {
      if (!selectedField) {
        setAvailableValues([]);
        return;
      }

      setLoading(true);
      try {
        const response = await apiFetch(`/database/field-values/${selectedField}`);
        if (response.success) {
          setAvailableValues(response.values || []);
        }
      } catch (error) {
        console.error('Error fetching field values:', error);
        setAvailableValues([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFieldValues();
  }, [selectedField]);

  const handleFieldChange = (e) => {
    setSelectedField(e.target.value);
    setSelectedValues([]); // Reset selected values when field changes
    setSelectedMatchStatuses([]); // Reset match statuses when field changes
    setSearchTerm(''); // Reset search
  };

  const handleValueToggle = (value) => {
    setSelectedValues(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleMatchStatusToggle = (status) => {
    setSelectedMatchStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const handleApply = () => {
    // Require at least field selected and either values or match statuses
    if (selectedField && (selectedValues.length > 0 || selectedMatchStatuses.length > 0)) {
      onApply({
        fieldName: selectedField,
        values: selectedValues,
        matchStatuses: selectedMatchStatuses
      });
    }
    onClose();
  };

  const handleClear = () => {
    setSelectedField('');
    setSelectedValues([]);
    setSelectedMatchStatuses([]);
    setSearchTerm('');
    onApply(null); // Clear filters
    onClose();
  };

  // Filter values by search term
  const filteredValues = availableValues.filter(item => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return item.value.toLowerCase().includes(searchLower);
  });

  if (!isOpen) return null;

  return (
    <ModalPortal isOpen={isOpen}>
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              <Filter size={20} />
              Advanced Filters
            </ModalTitle>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {/* Field Selection */}
            <Section>
              <Label>Select Field</Label>
              <Select value={selectedField} onChange={handleFieldChange}>
                <option value="">-- Choose a field --</option>
                {activeFields.map(field => (
                  <option key={field.field_uuid || field.name} value={field.name}>
                    {field.displayName || field.name}
                  </option>
                ))}
              </Select>
            </Section>

            {/* Match Status Selection */}
            {selectedField && (
              <Section>
                <Label>Match Status (optional)</Label>
                <MatchStatusGrid>
                  <MatchStatusOption
                    selected={selectedMatchStatuses.includes('perfect')}
                    color="#22c55e"
                    onClick={() => handleMatchStatusToggle('perfect')}
                  >
                    <MatchStatusColor color="#22c55e">
                      {selectedMatchStatuses.includes('perfect') && '✓'}
                    </MatchStatusColor>
                    <MatchStatusLabel>Match</MatchStatusLabel>
                  </MatchStatusOption>
                  
                  <MatchStatusOption
                    selected={selectedMatchStatuses.includes('maybe')}
                    color="#f59e0b"
                    onClick={() => handleMatchStatusToggle('maybe')}
                  >
                    <MatchStatusColor color="#f59e0b">
                      {selectedMatchStatuses.includes('maybe') && '✓'}
                    </MatchStatusColor>
                    <MatchStatusLabel>Partial Match</MatchStatusLabel>
                  </MatchStatusOption>
                  
                  <MatchStatusOption
                    selected={selectedMatchStatuses.includes('mismatch')}
                    color="#ef4444"
                    onClick={() => handleMatchStatusToggle('mismatch')}
                  >
                    <MatchStatusColor color="#ef4444">
                      {selectedMatchStatuses.includes('mismatch') && '✓'}
                    </MatchStatusColor>
                    <MatchStatusLabel>No Match</MatchStatusLabel>
                  </MatchStatusOption>
                  
                  <MatchStatusOption
                    selected={selectedMatchStatuses.includes('no_data')}
                    color="#d1fae5"
                    onClick={() => handleMatchStatusToggle('no_data')}
                  >
                    <MatchStatusColor color="#d1fae5" style={{ color: '#000' }}>
                      {selectedMatchStatuses.includes('no_data') && '✓'}
                    </MatchStatusColor>
                    <MatchStatusLabel>No Data</MatchStatusLabel>
                  </MatchStatusOption>
                </MatchStatusGrid>
              </Section>
            )}

            {/* Value Selection */}
            {selectedField && (
              <Section>
                <Label>Select Values ({selectedValues.length} selected)</Label>
                
                {/* Search */}
                <SearchContainer>
                  <SearchIcon>
                    <Search size={16} />
                  </SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Search values..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </SearchContainer>

                {/* Values List */}
                {loading ? (
                  <LoadingState>Loading values...</LoadingState>
                ) : filteredValues.length === 0 ? (
                  <EmptyState>
                    {searchTerm ? 'No matching values found' : 'No values available for this field'}
                  </EmptyState>
                ) : (
                  <ValuesList>
                    {filteredValues.map((item, index) => (
                      <ValueItem
                        key={index}
                        selected={selectedValues.includes(item.value)}
                        onClick={() => handleValueToggle(item.value)}
                      >
                        <ValueText>{item.value || '(empty)'}</ValueText>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <ValueCount>{item.count} loans</ValueCount>
                          {selectedValues.includes(item.value) && (
                            <CheckIcon>
                              <Check size={16} />
                            </CheckIcon>
                          )}
                        </div>
                      </ValueItem>
                    ))}
                  </ValuesList>
                )}
              </Section>
            )}
          </ModalBody>

          <ModalFooter>
            <SelectedCount>
              {selectedValues.length > 0 || selectedMatchStatuses.length > 0 
                ? `${selectedValues.length} value(s), ${selectedMatchStatuses.length} status(es) selected`
                : 'No filters selected'}
            </SelectedCount>
            <ButtonGroup>
              <Button variant="secondary" onClick={handleClear}>
                Clear Filters
              </Button>
              <Button 
                variant="primary" 
                onClick={handleApply}
                disabled={!selectedField || (selectedValues.length === 0 && selectedMatchStatuses.length === 0)}
              >
                Apply Filters
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </ModalPortal>
  );
};

export default AdvancedFiltersModal;

